import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { AlertTriangle, Send, Loader2 } from "lucide-react";

/**
 * Editorial-style confirmation dialog used before any irreversible or
 * externally-visible operation (e.g. sending an autofattura to SDI, marking
 * something as manually transmitted, bulk actions).
 *
 * Renders on top of the app; wired via <Dialog open onOpenChange>.
 */
export default function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel = "Conferma",
  cancelLabel = "Annulla",
  tone = "primary", // primary | danger
  loading = false,
  icon: Icon = AlertTriangle,
  extra,
  testid,
}) {
  const btn = {
    primary: "bg-emerald-600 hover:bg-emerald-700",
    danger: "bg-red-600 hover:bg-red-700",
    ink: "bg-[#0A0A0A] hover:bg-[#1D4ED8]",
  }[tone] || "bg-emerald-600 hover:bg-emerald-700";

  const iconBg = {
    primary: "bg-emerald-100 text-emerald-700",
    danger: "bg-red-100 text-red-600",
    ink: "bg-[#F5F2EA] text-[#0A0A0A]",
  }[tone] || "bg-emerald-100 text-emerald-700";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[440px] rounded-2xl border-[#E8E4D9]"
        data-testid={testid || "confirm-dialog"}
      >
        <DialogHeader className="items-start">
          <div className={`w-11 h-11 rounded-xl ${iconBg} grid place-items-center mb-3`}>
            <Icon className="w-5 h-5" />
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight text-[#0A0A0A] text-left">
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-sm text-[#4A4A4A] leading-relaxed text-left pt-1">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        {extra && <div className="pt-2">{extra}</div>}
        <DialogFooter className="pt-4 flex-col-reverse sm:flex-row gap-2 sm:gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            data-testid="confirm-dialog-cancel"
            className="w-full sm:w-auto px-4 py-2.5 rounded-md border border-[#E8E4D9] bg-white text-sm font-medium text-[#4A4A4A] hover:bg-[#F5F2EA] transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            data-testid="confirm-dialog-confirm"
            className={`w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md text-white text-sm font-semibold ${btn} disabled:opacity-70 transition-colors`}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
